import { useCallback, useState } from "react";
import useSWR, { SWRConfiguration, SWRResponse } from "swr";
import { OpenAPIConfig, SpotProviderApiClient } from "./generatedApi/spotProviderApi";
import { SpotSearchEngineApiClient } from "./generatedApi/spotSearchEngineApi";
import { UserInteractionLoggerApiClient } from "./generatedApi/userUserInteractionLoggerApi";
import { UtilProviderApiClient } from "./generatedApi/utilProviderApi";
import { UnifiedApiClient } from "./generatedApi/unifiedApi";
import { LineConnectorApiClient } from "./generatedApi/lineConnectorApi";

export const targetEnv = import.meta.env.VITE_APP_ENV;

if (!targetEnv) {
  throw new Error("VITE_APP_ENV is not defined. Please set it in your environment variables.");
}

// Demo API endpoints for demo app (from environment variables or fallback)
export const demoApiHost: Record<string, string> = {
  sb: "/v0",
  test:
    import.meta.env.VITE_DEMO_API_ENDPOINT || "https://demo-api.test.core.refloop.nicprdcts.com/v0",
  dev:
    import.meta.env.VITE_DEMO_API_ENDPOINT || "https://demo-api.dev.core.refloop.nicprdcts.com/v0",
  alpha:
    import.meta.env.VITE_DEMO_API_ENDPOINT ||
    "https://demo-api.alpha.core.refloop.nicprdcts.com/v0",
  stg:
    import.meta.env.VITE_DEMO_API_ENDPOINT || "https://demo-api.stg.core.refloop.nicprdcts.com/v0",
  prd: import.meta.env.VITE_DEMO_API_ENDPOINT || "https://demo-api.core.refloop.nicprdcts.com/v0"
};

// External API endpoints (認証不要)
export const externalApiHost: Record<string, string> = {
  sb: "/v0",
  test: "https://external-api.test.core.refloop.nicprdcts.com/v0",
  dev: "https://external-api.dev.core.refloop.nicprdcts.com/v0",
  alpha: "https://external-api.alpha.core.refloop.nicprdcts.com/v0",
  stg: "https://external-api.stg.core.refloop.nicprdcts.com/v0",
  prd: "https://external-api.core.refloop.nicprdcts.com/v0"
};

type Constructor<T = any> = new (...args: any[]) => T;

export const apiNameConstructorMap = {
  spot_provider: SpotProviderApiClient,
  spot_search_engine: SpotSearchEngineApiClient,
  user_interaction_logger: UserInteractionLoggerApiClient,
  util_provider: UtilProviderApiClient,
  user_config: UnifiedApiClient,
  line_connector: LineConnectorApiClient
};

export type ApiName = keyof typeof apiNameConstructorMap;

export type NonNullableData<T> = T extends void | undefined | null ? "__fetchSuccess__" : T;

export type ClientClassConstructor<S extends ApiName> =
  (typeof apiNameConstructorMap)[S] extends Constructor ? (typeof apiNameConstructorMap)[S] : never;

export type ClientServiceName<
  S extends ApiName,
  T extends ClientClassConstructor<S>
> = keyof InstanceType<T>;

export type ClientService<
  S extends ApiName,
  T extends ClientClassConstructor<S>,
  U extends ClientServiceName<S, T>
> = InstanceType<T>[U];

export type ClientOperationId<
  S extends ApiName,
  T extends ClientClassConstructor<S>,
  U extends ClientServiceName<S, T>,
  V extends ClientService<S, T, U>
> = keyof V;

export type Fetcher = <
  S extends ApiName,
  T extends ClientClassConstructor<S>,
  U extends ClientServiceName<S, T>,
  V extends ClientService<S, T, U>,
  W extends ClientOperationId<S, T, U, V>
>(
  // ClientConfiguration: ClientConfigurationConstructor,
  apiName: S,
  serviceName: U,
  operationId: W,
  requestParameters: Parameters<V[W]>[0]
  // initOverrides?: Parameters<InstanceType<T>[U]>[1]
) => Promise<{
  data: NonNullableData<Awaited<ReturnType<V[W]>>> | undefined;
  error: any;
}>;

export const useDemoFetcher = () => {
  const fetcher: Fetcher = async (apiName, serviceName, operationId, requestParameters) => {
    const basePath = apiName;

    let token = "";

    // In Storybook environment, use a mock token
    if (targetEnv === "sb") {
      token = "mock-storybook-token";
    } else {
      try {
        const { fetchAuthSession } = await import("aws-amplify/auth");
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || "";
      } catch (error) {
        console.warn("Failed to fetch auth session:", error);
      }
    }

    const params: Partial<OpenAPIConfig> = {
      BASE: `${demoApiHost[targetEnv]}/${basePath}`,
      TOKEN: token
    };

    const cli = new apiNameConstructorMap[apiName](params);
    try {
      // @ts-expect-error - TypeScript can't infer the correct method signature for dynamic API calls
      const res = await cli[serviceName][operationId](requestParameters);
      return { data: res, error: null };
    } catch (e) {
      return { data: undefined, error: e };
    }
  };
  return fetcher;
};

export type Sender = <
  S extends ApiName,
  T extends ClientClassConstructor<S>,
  U extends ClientServiceName<S, T>,
  V extends ClientService<S, T, U>,
  W extends ClientOperationId<S, T, U, V>
>(
  // ClientConfiguration: ClientConfigurationConstructor,
  apiName: S,
  serviceName: U,
  operationId: W
  // initOverrides?: Parameters<InstanceType<T>[U]>[1]
) => Promise<{
  data: NonNullableData<Awaited<ReturnType<V[W]>>> | undefined;
  error: any;
  sender: (args: Parameters<V[W]>[0]) => Promise<NonNullableData<Awaited<ReturnType<V[W]>>>>;
}>;

export type useDemoSenderProps<
  S extends ApiName,
  T extends ClientClassConstructor<S>,
  U extends ClientServiceName<S, T>,
  V extends ClientService<S, T, U>,
  W extends ClientOperationId<S, T, U, V>
> = {
  apiName: S;
  serviceName: U;
  operationId: W;
};

export const useDemoSender = <
  S extends ApiName,
  T extends ClientClassConstructor<S>,
  U extends ClientServiceName<S, T>,
  V extends ClientService<S, T, U>,
  W extends ClientOperationId<S, T, U, V>
>({
  apiName,
  serviceName,
  operationId
}: useDemoSenderProps<S, T, U, V, W>): {
  data: NonNullableData<Awaited<ReturnType<V[W]>>> | undefined;
  error: any;
  isLoading: boolean;
  sender: (args: Parameters<V[W]>[0]) => Promise<NonNullableData<ReturnType<V[W]>> | undefined>;
} => {
  const [data, setData] = useState<NonNullableData<Awaited<ReturnType<V[W]>>> | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const fetcher = useDemoFetcher();

  const sender = useCallback(
    async (
      requestData: Parameters<V[W]>[0]
    ): Promise<NonNullableData<ReturnType<V[W]>> | undefined> => {
      setIsLoading(true);
      setError(null);
      setData(undefined);
      const { data: res, error: err } = await fetcher(
        apiName,
        serviceName,
        operationId,
        requestData
      );
      if (err) {
        setError(err);
      } else {
        setData(res);
      }
      setIsLoading(false);
      return res;
    },
    // NOTE: Do not add 'fetcher' to dependencies as it's recreated on every render causing infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiName, serviceName, operationId]
  );

  return { data, isLoading, error, sender };
};

export type useDemoFetcherWithSrwProps<
  S extends ApiName,
  T extends ClientClassConstructor<S>,
  U extends ClientServiceName<S, T>,
  V extends ClientService<S, T, U>,
  W extends ClientOperationId<S, T, U, V>
> = {
  apiName: S;
  serviceName: U;
  operationId: W;
  requestParameters: Parameters<V[W]>[0];
  swrConfiguration?: SWRConfiguration;
  enabled?: boolean;
};

export const useDemoFetcherWithSwr = <
  S extends ApiName,
  T extends ClientClassConstructor<S>,
  U extends ClientServiceName<S, T>,
  V extends ClientService<S, T, U>,
  W extends ClientOperationId<S, T, U, V>
>({
  apiName,
  serviceName,
  operationId,
  requestParameters,
  swrConfiguration,
  enabled = true
}: useDemoFetcherWithSrwProps<S, T, U, V, W>): SWRResponse<
  NonNullableData<Awaited<ReturnType<V[W]>>>,
  any,
  any
> & { cacheKey: string } => {
  const cacheKey = `${apiName}.${serviceName as string}.${operationId as string}.${JSON.stringify(
    requestParameters
  )}`;
  const fetcher = useDemoFetcher();
  const apiCall = async () => {
    const { data: res, error: err } = await fetcher(
      apiName,
      serviceName,
      operationId,
      requestParameters
    );
    if (err) {
      throw err;
    }
    return res;
  };
  // enabled が false の場合は key を null にして SWR を無効化
  const swrRes = useSWR(enabled ? cacheKey : null, apiCall, swrConfiguration);
  // @ts-expect-error TS2322
  return { ...swrRes, cacheKey };
};
